import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FaFacebookMessenger, FaWhatsapp } from 'react-icons/fa';
import { AiOutlineCopy, AiOutlineDelete, AiOutlinePlus, AiOutlineReload, AiOutlineSend, AiOutlineLink } from 'react-icons/ai';
import myAxios from '../AxiosUrl';
import styles from './ChatAutomation.module.css';

/**
 * Messenger & WhatsApp automation (rule-based, no AI) — every reply is written by the seller.
 * Backend: EtechBackend/controllars/ChatAutomationController.js, utils/ChatAutomation/.
 */

const EVENTS = [
    ['placed', 'Order received', 'Sent when a new order comes in (status New)'],
    ['confirmed', 'Order confirmed', 'Sent when the order moves to Call Done'],
    ['shipped', 'Sent to courier', 'Sent when the order moves to Courier Done (with the Steadfast tracking code)'],
];

const fmt = (v) => (v ? new Date(v).toLocaleString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '—');
const errMsg = (err, fallback) => err?.response?.data?.message || err?.message || fallback;
const lines = (arr) => (arr || []).join('\n');
const commas = (arr) => (arr || []).join(', ');

function toForm(s) {
    return {
        channels: { messenger: !!s.channels?.messenger, whatsapp: !!s.channels?.whatsapp },
        handoverHours: s.handoverHours ?? 6,
        welcome: { enabled: !!s.welcome?.enabled, text: s.welcome?.text || '' },
        keywords: (s.keywords || []).map((k) => ({ _id: k._id, words: commas(k.words), reply: k.reply || '', match: k.match || 'contains', channels: k.channels?.length ? k.channels : ['messenger', 'whatsapp'], enabled: k.enabled !== false })),
        productReply: { enabled: !!s.productReply?.enabled, text: s.productReply?.text || '', aliases: lines(s.productReply?.aliases) },
        orderFlow: { enabled: !!s.orderFlow?.enabled, triggers: commas(s.orderFlow?.triggers), texts: { ...(s.orderFlow?.texts || {}) } },
        photoReply: { enabled: !!s.photoReply?.enabled, text: s.photoReply?.text || '' },
        away: { enabled: !!s.away?.enabled, text: s.away?.text || '', open: s.away?.open || '10:00', close: s.away?.close || '22:00' },
        fallback: { enabled: !!s.fallback?.enabled, text: s.fallback?.text || '' },
        comments: {
            enabled: !!s.comments?.enabled,
            publicReplies: lines(s.comments?.publicReplies),
            onlyWords: commas(s.comments?.onlyWords),
            inbox: s.comments?.inbox !== false,
            inboxText: s.comments?.inboxText || '',
            productInfo: s.comments?.productInfo !== false,
        },
        waUpdates: {
            enabled: !!s.waUpdates?.enabled,
            ...Object.fromEntries(EVENTS.map(([k]) => [k, { enabled: !!s.waUpdates?.[k]?.enabled, text: s.waUpdates?.[k]?.text || '' }])),
        },
    };
}

function toBody(f) {
    return {
        ...f,
        keywords: f.keywords.map((k) => ({ ...k, words: k.words.split(',').map((w) => w.trim()).filter(Boolean) })),
        productReply: { ...f.productReply, aliases: f.productReply.aliases.split('\n') },
        orderFlow: { ...f.orderFlow, triggers: f.orderFlow.triggers.split(',').map((w) => w.trim()).filter(Boolean) },
        comments: { ...f.comments, publicReplies: f.comments.publicReplies.split('\n'), onlyWords: f.comments.onlyWords.split(',').map((w) => w.trim()).filter(Boolean) },
    };
}

function Switch({ on, onChange, title, desc }) {
    return (
        <div className={styles.switchRow}>
            <button type="button" role="switch" aria-checked={on} className={`${styles.switch} ${on ? styles.switchOn : ''}`} onClick={() => onChange(!on)} />
            <div className={styles.switchText}>
                <span className={styles.switchTitle}>{title}</span>
                {desc && <span className={styles.switchDesc}>{desc}</span>}
            </div>
        </div>
    );
}

function Pill({ ok, yes = 'Done', no = 'Not yet', warn }) {
    const cls = ok ? styles.pillGreen : warn ? styles.pillYellow : styles.pillRed;
    return <span className={`${styles.pill} ${cls}`}>{ok ? yes : no}</span>;
}

function Text({ label, value, onChange, placeholder, rows = 3, hint }) {
    return (
        <div className={styles.field}>
            {label && <span className={styles.fieldLabel}>{label}</span>}
            <textarea className={styles.textarea} rows={rows} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
            {hint && <span className={styles.hint}>{hint}</span>}
        </div>
    );
}

function ChatAutomation() {
    const [data, setData] = useState(null);
    const [form, setForm] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [dirty, setDirty] = useState(false);
    const [connecting, setConnecting] = useState(false);
    const [connectErrors, setConnectErrors] = useState([]);
    const [appSecret, setAppSecret] = useState('');
    const [testChannel, setTestChannel] = useState('messenger');
    const [testText, setTestText] = useState('');
    const [testLog, setTestLog] = useState([]);
    const [testing, setTesting] = useState(false);
    const [threads, setThreads] = useState([]);
    const [openThread, setOpenThread] = useState(null);
    const [threadMsgs, setThreadMsgs] = useState([]);
    const [activity, setActivity] = useState({ comments: [], waUpdates: [] });
    const chatEnd = useRef(null);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const res = await myAxios.get('chat-automation');
            setData(res.data);
            setForm(toForm(res.data.settings));
            setDirty(false);
        } catch (err) {
            toast.error(errMsg(err, 'Could not load the automation settings'));
        } finally {
            setLoading(false);
        }
    }, []);

    const loadThreads = useCallback(async () => {
        try {
            const [t, a] = await Promise.all([myAxios.get('chat-automation/threads'), myAxios.get('chat-automation/activity')]);
            setThreads(t.data.threads || []);
            setActivity({ comments: a.data.comments || [], waUpdates: a.data.waUpdates || [] });
        } catch (err) {
            /* the lists are optional */
        }
    }, []);

    useEffect(() => {
        load();
        loadThreads();
    }, [load, loadThreads]);

    useEffect(() => {
        chatEnd.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }, [testLog]);

    const up = (path, value) => {
        setForm((f) => {
            const next = JSON.parse(JSON.stringify(f));
            const keys = path.split('.');
            let o = next;
            for (const k of keys.slice(0, -1)) o = o[k];
            o[keys[keys.length - 1]] = value;
            return next;
        });
        setDirty(true);
    };

    const save = async () => {
        try {
            setSaving(true);
            const res = await myAxios.put('chat-automation', toBody(form));
            setData((d) => ({ ...d, settings: res.data.settings }));
            setForm(toForm(res.data.settings));
            setDirty(false);
            toast.success('Saved');
            return true;
        } catch (err) {
            toast.error(errMsg(err, 'Could not save'));
            return false;
        } finally {
            setSaving(false);
        }
    };

    const connect = async () => {
        try {
            setConnecting(true);
            const res = await myAxios.post('chat-automation/connect', appSecret ? { appSecret } : {});
            setData((d) => ({ ...d, settings: res.data.settings }));
            setConnectErrors(res.data.errors || []);
            setAppSecret('');
            toast[(res.data.errors || []).length ? 'warn' : 'success']((res.data.errors || []).length ? 'Connected with some problems — see below' : 'Connected');
            load();
        } catch (err) {
            toast.error(errMsg(err, 'Could not connect'));
        } finally {
            setConnecting(false);
        }
    };

    const submitTemplates = async () => {
        if (dirty && !(await save())) return;
        try {
            const res = await myAxios.post('chat-automation/wa-templates');
            setData((d) => ({ ...d, settings: res.data.settings }));
            toast.success('Sent to Meta for approval. It usually takes a few minutes.');
        } catch (err) {
            toast.error(errMsg(err, 'Could not submit to Meta'));
        }
    };

    const sendTest = async (photo = false) => {
        if (!photo && !testText.trim()) return;
        if (dirty && !(await save())) return;
        const text = testText.trim();
        setTestLog((l) => [...l, { me: true, text: photo ? '📷 [photo]' : text }]);
        setTestText('');
        try {
            setTesting(true);
            const res = await myAxios.post('chat-automation/test', { text, channel: testChannel, photo });
            const replies = res.data.replies || [];
            setTestLog((l) => [...l, ...(replies.length ? replies.map((r) => (r.type === 'image' ? { text: `🖼️ photo: ${r.url}` } : { text: r.text })) : [{ note: true, text: res.data.result?.paused ? 'No reply (bot paused in this chat)' : 'No automatic reply for this message' }])]);
        } catch (err) {
            toast.error(errMsg(err, 'Test failed'));
        } finally {
            setTesting(false);
        }
    };

    const resetTest = async () => {
        await myAxios.post('chat-automation/test', { reset: true, channel: testChannel }).catch(() => {});
        setTestLog([]);
    };

    const openChat = async (t) => {
        if (openThread?._id === t._id) {
            setOpenThread(null);
            return;
        }
        try {
            const res = await myAxios.get(`chat-automation/threads/${t._id}`);
            setOpenThread(res.data.thread);
            setThreadMsgs(res.data.messages || []);
        } catch (err) {
            toast.error(errMsg(err, 'Could not open the chat'));
        }
    };

    const pause = async (t, hours) => {
        try {
            await myAxios.post(`chat-automation/threads/${t._id}/pause`, { hours });
            toast.success(hours ? `Bot paused in this chat for ${hours} h` : 'Bot active again in this chat');
            loadThreads();
        } catch (err) {
            toast.error(errMsg(err, 'Could not change the chat'));
        }
    };

    const copy = async (text) => {
        try {
            await navigator.clipboard.writeText(text);
            toast.success('Copied');
        } catch (e) {
            toast.error('Copy failed');
        }
    };

    if (loading || !form || !data) {
        return (
            <div className={styles.container}>
                <div className={styles.card}>
                    <div className={`${styles.skeleton} ${styles.skeletonLine}`} />
                </div>
            </div>
        );
    }

    const s = data.settings;
    const d = data.defaults || {};
    const meta = data.meta || {};
    const health = data.whatsappHealth;
    const missingAll = Object.entries(meta.missing || {}).filter(([, v]) => v.length);

    return (
        <div className={styles.container}>
            <ToastContainer position="top-right" autoClose={3000} />

            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <div className={`${styles.headerIcon} ${styles.headerIconChat}`}>
                        <FaFacebookMessenger />
                    </div>
                    <div>
                        <h1 className={styles.title}>Messenger & WhatsApp</h1>
                        <p className={styles.subtitle}>
                            Automatic replies you write yourself: welcome, keywords, product prices, orders in chat, Facebook comments and WhatsApp order updates
                        </p>
                    </div>
                </div>
            </div>

            {/* ───────── connection ───────── */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>1. Connect your Page and WhatsApp</h2>
                    <div className={styles.cardActions}>
                        <button type="button" className={styles.btnPrimary} onClick={connect} disabled={connecting || !meta.tokenSaved}>
                            <AiOutlineLink /> {connecting ? 'Connecting…' : s.connect?.at ? 'Connect again' : 'Connect'}
                        </button>
                    </div>
                </div>
                <div className={styles.checklist}>
                    <div className={styles.checkRow}>
                        <span className={styles.label}>Meta token</span>
                        <div>
                            <Pill ok={meta.tokenSaved && meta.pageChosen} yes="Saved" no={meta.tokenSaved ? 'No Page chosen' : 'Not saved'} />{' '}
                            <Link className={styles.link} to="/meta-analytics">Meta Analytics</Link>
                            {missingAll.length > 0 && (
                                <ul className={styles.problems}>
                                    {missingAll.map(([group, perms]) => (
                                        <li key={group}>{group}: token is missing {perms.join(', ')}</li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className={styles.checkRow}>
                        <span className={styles.label}>Facebook Page</span>
                        <div>
                            <Pill ok={s.connect?.pageSubscribed} yes="Connected" /> <span className={styles.value}>{s.pageName || s.pageId || ''}</span>
                        </div>
                    </div>
                    <div className={styles.checkRow}>
                        <span className={styles.label}>WhatsApp number</span>
                        <div>
                            <Pill ok={s.connect?.wabaSubscribed} yes="Connected" /> <span className={styles.value}>{s.waPhone || ''}</span>
                            {health?.problems?.length > 0 && (
                                <ul className={styles.problems}>
                                    {health.problems.map((p, i) => (
                                        <li key={i}>
                                            {p.text} {p.fix ? `— ${p.fix}` : ''}
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <div className={styles.checkRow}>
                        <span className={styles.label}>Webhook (receive messages)</span>
                        <div>
                            <Pill ok={s.connect?.appWebhook} yes="Set automatically" no="Set it in your app" warn />{' '}
                            <span className={styles.muted}>Last message received: {fmt(s.lastWebhookAt)}</span>
                            <div className={styles.urlRow} style={{ marginTop: 8 }}>
                                <input className={styles.urlInput} readOnly value={s.hookUrl} />
                                <button type="button" className={styles.btnSecondary} onClick={() => copy(s.hookUrl)}>
                                    <AiOutlineCopy /> URL
                                </button>
                            </div>
                            <div className={styles.urlRow} style={{ marginTop: 8 }}>
                                <input className={styles.urlInput} readOnly value={s.verifyToken} />
                                <button type="button" className={styles.btnSecondary} onClick={() => copy(s.verifyToken)}>
                                    <AiOutlineCopy /> Verify token
                                </button>
                            </div>
                        </div>
                    </div>
                    <div className={styles.checkRow}>
                        <span className={styles.label}>App secret (optional)</span>
                        <div>
                            <input className={styles.input} type="password" autoComplete="off" placeholder={s.appSecretSet ? 'Saved — paste a new one to change it' : 'developers.facebook.com → your app → App settings → Basic → App secret'} value={appSecret} onChange={(e) => setAppSecret(e.target.value.trim())} />
                            <span className={styles.hint}>With the app secret, Connect also sets the webhook in your app and every message from Meta is checked with it.</span>
                        </div>
                    </div>
                </div>
                {connectErrors.length > 0 && (
                    <div className={styles.warn} style={{ marginTop: 12 }}>
                        {connectErrors.map((e, i) => (
                            <div key={i}>{e}</div>
                        ))}
                    </div>
                )}
                <details className={styles.details}>
                    <summary>Before customers get replies (Meta rules)</summary>
                    <ol className={styles.steps}>
                        <li>Your Meta app must be <strong>Live</strong> (developers.facebook.com → your app → App settings → Basic → App mode: Live).</li>
                        <li>Without the app secret: in your app open the Messenger and WhatsApp webhook settings and paste the URL and verify token above (fields: messages, messaging_postbacks, message_echoes, feed; WhatsApp: messages, smb_message_echoes).</li>
                        <li>Messenger replies to customers need <strong>Advanced Access</strong> for pages_messaging (Business Verification + App Review). Until then only people with a role on your Page get replies.</li>
                        <li>WhatsApp order updates need a working payment method and a timezone on your WhatsApp account (Meta charges per message).</li>
                    </ol>
                </details>
            </div>

            {/* ───────── on / off ───────── */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>2. Turn on</h2>
                    <span className={styles.muted}>
                        {data.counts?.threads || 0} chats · {data.counts?.botRepliesLast24h || 0} automatic replies in the last 24 h · {s.stats?.orders || 0} orders from chat
                    </span>
                </div>
                <div className={styles.twoCol}>
                    <Switch on={form.channels.messenger} onChange={(v) => up('channels.messenger', v)} title={<><FaFacebookMessenger /> Messenger replies</>} desc="Automatic replies in your Facebook Page inbox" />
                    <Switch on={form.channels.whatsapp} onChange={(v) => up('channels.whatsapp', v)} title={<><FaWhatsapp /> WhatsApp replies</>} desc="Automatic replies on your WhatsApp number" />
                </div>
                <div className={styles.field}>
                    <span className={styles.fieldLabel}>When you reply yourself, the bot stays quiet in that chat for</span>
                    <div className={styles.inline}>
                        <input className={`${styles.input} ${styles.small}`} type="number" min="0" max="168" value={form.handoverHours} onChange={(e) => up('handoverHours', e.target.value)} />
                        <span className={styles.muted}>hours</span>
                    </div>
                </div>
            </div>

            <div className={styles.sectionGrid}>
                {/* welcome */}
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Welcome message</h2>
                    <Switch on={form.welcome.enabled} onChange={(v) => up('welcome.enabled', v)} title="Send a welcome" desc="To a customer's first message, and again after 24 hours of silence" />
                    <Text value={form.welcome.text} onChange={(v) => up('welcome.text', v)} placeholder={d.welcome} hint="{name} = customer's first name, {store} = your store name. Empty = the text shown in grey." />
                </div>

                {/* product replies */}
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Product price replies</h2>
                    <Switch on={form.productReply.enabled} onChange={(v) => up('productReply.enabled', v)} title="Answer with the product's price" desc="When a message names one of your products (name or code): price, stock, colours/sizes, link and photo from your store" />
                    <Text value={form.productReply.text} onChange={(v) => up('productReply.text', v)} placeholder={d.productReply} rows={4} hint="{product} {price} {old_price} {stock} {options} {link} {code}" />
                    <Text label="Other words customers use (one group per line)" value={form.productReply.aliases} onChange={(v) => up('productReply.aliases', v)} placeholder={'সাবান = bar, soap\nক্রিম = cream'} hint="Words on one line mean the same thing. Common ones are built in." />
                    <details className={styles.details}>
                        <summary>Built-in words</summary>
                        <div className={styles.hint}>{(d.wordGroups || []).join(' · ')}</div>
                    </details>
                </div>
            </div>

            {/* keywords */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Keyword replies</h2>
                    <button type="button" className={styles.btnSecondary} onClick={() => up('keywords', [...form.keywords, { words: '', reply: '', match: 'contains', channels: ['messenger', 'whatsapp'], enabled: true }])}>
                        <AiOutlinePlus /> Add keyword
                    </button>
                </div>
                <p className={styles.hint}>When a customer's message has one of the words, your reply goes out. Checked before product replies. Example: ডেলিভারি চার্জ, delivery → “ঢাকার ভেতরে ৭০ টাকা, বাইরে ১৩০ টাকা”.</p>
                {form.keywords.length === 0 && <p className={styles.muted}>No keyword replies yet.</p>}
                {form.keywords.map((k, i) => (
                    <div key={k._id || i} className={styles.rule}>
                        <div className={styles.ruleTop}>
                            <input className={styles.input} placeholder="Words, separated by commas" value={k.words} onChange={(e) => up(`keywords.${i}.words`, e.target.value)} />
                            <select className={styles.select} value={k.match} onChange={(e) => up(`keywords.${i}.match`, e.target.value)}>
                                <option value="contains">Message contains</option>
                                <option value="exact">Message is exactly</option>
                            </select>
                            <button type="button" className={styles.iconBtn} title="Delete" onClick={() => up('keywords', form.keywords.filter((_, j) => j !== i))}>
                                <AiOutlineDelete />
                            </button>
                        </div>
                        <Text value={k.reply} onChange={(v) => up(`keywords.${i}.reply`, v)} placeholder="Your reply ({name} and {store} work here)" rows={2} />
                        <div className={styles.checks} style={{ marginTop: 8 }}>
                            {['messenger', 'whatsapp'].map((c) => (
                                <label key={c}>
                                    <input type="checkbox" checked={k.channels.includes(c)} onChange={(e) => up(`keywords.${i}.channels`, e.target.checked ? [...k.channels, c] : k.channels.filter((x) => x !== c))} />
                                    {c === 'messenger' ? 'Messenger' : 'WhatsApp'}
                                </label>
                            ))}
                            <label>
                                <input type="checkbox" checked={k.enabled} onChange={(e) => up(`keywords.${i}.enabled`, e.target.checked)} /> On
                            </label>
                        </div>
                    </div>
                ))}
            </div>

            {/* order form */}
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Take orders in chat</h2>
                <Switch on={form.orderFlow.enabled} onChange={(v) => up('orderFlow.enabled', v)} title="Order form inside the chat" desc="The customer writes “অর্ডার”: the bot asks product → colour/size → quantity → name → mobile → address → inside/outside Dhaka, shows the total and creates a New order in your store (source Messenger / WhatsApp)." />
                <div className={styles.field}>
                    <span className={styles.fieldLabel}>Words that start an order</span>
                    <input className={styles.input} value={form.orderFlow.triggers} placeholder={commas(d.orderTriggers)} onChange={(e) => up('orderFlow.triggers', e.target.value)} />
                </div>
                <details className={styles.details}>
                    <summary>Edit the order form messages</summary>
                    {Object.entries(d.orderTexts || {}).map(([key, def]) => (
                        <Text key={key} label={d.orderTextLabels?.[key] || key} value={form.orderFlow.texts[key] || ''} onChange={(v) => up(`orderFlow.texts.${key}`, v)} placeholder={def} rows={2} />
                    ))}
                </details>
            </div>

            <div className={styles.sectionGrid}>
                {/* away + fallback + photo */}
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Away message</h2>
                    <Switch on={form.away.enabled} onChange={(v) => up('away.enabled', v)} title="Outside opening hours" desc="Sent once per 12 hours per chat when nothing else answered" />
                    <div className={styles.inline} style={{ marginTop: 12 }}>
                        <span className={styles.muted}>Open</span>
                        <input className={`${styles.input} ${styles.small}`} type="time" value={form.away.open} onChange={(e) => up('away.open', e.target.value)} />
                        <span className={styles.muted}>to</span>
                        <input className={`${styles.input} ${styles.small}`} type="time" value={form.away.close} onChange={(e) => up('away.close', e.target.value)} />
                    </div>
                    <Text value={form.away.text} onChange={(v) => up('away.text', v)} placeholder={d.away} hint="{open} {close} {name} {store}" />
                </div>
                <div className={styles.card}>
                    <h2 className={styles.cardTitle}>Default reply & photos</h2>
                    <Switch on={form.fallback.enabled} onChange={(v) => up('fallback.enabled', v)} title="Default reply" desc="When nothing matched (once per 6 hours per chat)" />
                    <Text value={form.fallback.text} onChange={(v) => up('fallback.text', v)} placeholder={d.fallback} rows={2} />
                    <Switch on={form.photoReply.enabled} onChange={(v) => up('photoReply.enabled', v)} title="Photo reply" desc="When a customer sends only a photo (once per hour per chat)" />
                    <Text value={form.photoReply.text} onChange={(v) => up('photoReply.text', v)} placeholder={d.photoReply} rows={2} />
                </div>
            </div>

            {/* comments */}
            <div className={styles.card}>
                <h2 className={styles.cardTitle}>Facebook comments</h2>
                <Switch on={form.comments.enabled} onChange={(v) => up('comments.enabled', v)} title="Reply to comments on your posts" desc="A public reply under the comment and one private inbox message to the person (once per person per post, at most 120 an hour)" />
                <div className={styles.twoCol}>
                    <Text label="Public replies (one per line — a random one is used)" value={form.comments.publicReplies} onChange={(v) => up('comments.publicReplies', v)} placeholder={lines(d.commentPublic)} rows={3} hint="{name} = first name" />
                    <div className={styles.field}>
                        <span className={styles.fieldLabel}>Only comments with these words (empty = all)</span>
                        <input className={styles.input} value={form.comments.onlyWords} placeholder="দাম, price, কত, info" onChange={(e) => up('comments.onlyWords', e.target.value)} />
                    </div>
                </div>
                <Switch on={form.comments.inbox} onChange={(v) => up('comments.inbox', v)} title="Also send a private inbox message" desc="Needs Messenger Advanced Access for customers (see section 1)" />
                <Text value={form.comments.inboxText} onChange={(v) => up('comments.inboxText', v)} placeholder={d.commentInbox} hint="{name} {store} {product_info} = the product of that post (price, stock, link) when the post names one of your products" />
                <Switch on={form.comments.productInfo} onChange={(v) => up('comments.productInfo', v)} title="Find the post's product" desc="Fills {product_info} from your store" />
            </div>

            {/* whatsapp order updates */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>
                        <FaWhatsapp /> WhatsApp order updates
                    </h2>
                    <button type="button" className={styles.btnSecondary} onClick={submitTemplates} disabled={!s.wabaId}>
                        <AiOutlineSend /> Send to Meta for approval
                    </button>
                </div>
                <Switch on={form.waUpdates.enabled} onChange={(v) => up('waUpdates.enabled', v)} title="Message customers on WhatsApp when their order changes" desc="Meta approves each message first (usually minutes) and charges your WhatsApp account per message. Only orders placed after you turn this on." />
                {EVENTS.map(([key, title, desc]) => {
                    const ev = s.waUpdates?.[key] || {};
                    return (
                        <div key={key} className={styles.rule}>
                            <div className={styles.inline} style={{ justifyContent: 'space-between' }}>
                                <label className={styles.checks}>
                                    <input type="checkbox" checked={form.waUpdates[key].enabled} onChange={(e) => up(`waUpdates.${key}.enabled`, e.target.checked)} /> <strong>{title}</strong>
                                </label>
                                {ev.status ? <span className={`${styles.pill} ${ev.status === 'APPROVED' ? styles.pillGreen : ev.status === 'PENDING' ? styles.pillYellow : styles.pillRed}`}>{ev.status}</span> : <span className={`${styles.pill} ${styles.pillGrey}`}>Not sent to Meta</span>}
                            </div>
                            <span className={styles.hint}>{desc}</span>
                            <Text value={form.waUpdates[key].text} onChange={(v) => up(`waUpdates.${key}.text`, v)} placeholder={d.waUpdates?.[key]} rows={2} hint={`{name} {invoice} {total} {store} {tracking} — not at the very start or end. Changing the text needs a new approval.${ev.reason ? ` · ${ev.reason}` : ''}`} />
                        </div>
                    );
                })}
            </div>

            {/* test */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Test your bot</h2>
                    <div className={styles.cardActions}>
                        <select className={styles.select} value={testChannel} onChange={(e) => { setTestChannel(e.target.value); setTestLog([]); }}>
                            <option value="messenger">As Messenger</option>
                            <option value="whatsapp">As WhatsApp</option>
                        </select>
                        <button type="button" className={styles.btnSecondary} onClick={resetTest}>
                            <AiOutlineReload /> New chat
                        </button>
                    </div>
                </div>
                <p className={styles.hint}>Write like a customer. Nothing is sent to anyone and test orders are not saved. Unsaved changes are saved first.</p>
                <div className={styles.chatBox}>
                    {testLog.length === 0 && <span className={styles.muted}>Try: “আসসালামু আলাইকুম”, a product name, “অর্ডার”, “ডেলিভারি চার্জ কত”…</span>}
                    {testLog.map((m, i) => (
                        <div key={i} className={`${styles.bubble} ${m.me ? styles.bubbleIn : m.note ? styles.bubbleErr : styles.bubbleOut}`}>
                            {m.text}
                        </div>
                    ))}
                    <div ref={chatEnd} />
                </div>
                <div className={styles.urlRow} style={{ marginTop: 10 }}>
                    <input className={styles.urlInput} value={testText} placeholder="Customer message…" onChange={(e) => setTestText(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && sendTest()} />
                    <button type="button" className={styles.btnPrimary} onClick={() => sendTest()} disabled={testing}>
                        <AiOutlineSend /> Send
                    </button>
                    <button type="button" className={styles.btnSecondary} onClick={() => sendTest(true)} disabled={testing}>
                        📷 Photo
                    </button>
                </div>
            </div>

            {/* chats */}
            <div className={styles.card}>
                <div className={styles.cardHeader}>
                    <h2 className={styles.cardTitle}>Recent chats</h2>
                    <button type="button" className={styles.btnSecondary} onClick={loadThreads}>
                        <AiOutlineReload /> Refresh
                    </button>
                </div>
                {threads.length === 0 ? (
                    <p className={styles.muted}>No chats yet. They appear here once messages arrive with replies turned on.</p>
                ) : (
                    <div className={styles.tableWrap}>
                        <table className={styles.table}>
                            <thead>
                                <tr>
                                    <th>Customer</th>
                                    <th>Last message</th>
                                    <th>When</th>
                                    <th>Bot</th>
                                </tr>
                            </thead>
                            <tbody>
                                {threads.map((t) => (
                                    <React.Fragment key={t._id}>
                                        <tr className={styles.rowClickable} onClick={() => openChat(t)}>
                                            <td>
                                                {t.channel === 'whatsapp' ? <FaWhatsapp color="#25d366" /> : <FaFacebookMessenger color="#0866ff" />} {t.name || t.externalId}
                                                {t.orders?.length ? <span className={`${styles.pill} ${styles.pillGreen}`} style={{ marginLeft: 6 }}>{t.orders.length} order</span> : null}
                                            </td>
                                            <td>{t.lastText}</td>
                                            <td>{fmt(t.lastInboundAt)}</td>
                                            <td onClick={(e) => e.stopPropagation()}>
                                                {t.paused ? (
                                                    <button type="button" className={styles.btnSecondary} onClick={() => pause(t, 0)}>Resume</button>
                                                ) : (
                                                    <button type="button" className={styles.btnSecondary} onClick={() => pause(t, 24)}>Pause 24 h</button>
                                                )}
                                            </td>
                                        </tr>
                                        {openThread?._id === t._id && (
                                            <tr>
                                                <td colSpan={4}>
                                                    <div className={styles.chatBox}>
                                                        {threadMsgs.map((m) => (
                                                            <div key={m._id} className={`${styles.bubble} ${m.dir === 'in' ? styles.bubbleIn : m.error ? styles.bubbleErr : m.by === 'human' ? styles.bubbleHuman : styles.bubbleOut}`}>
                                                                {m.text}
                                                                <span className={styles.bubbleMeta}>
                                                                    {m.dir === 'in' ? 'customer' : m.by === 'human' ? 'you' : `bot · ${m.kind}`} · {fmt(m.at)}
                                                                    {m.error ? ` · not sent: ${m.error}` : ''}
                                                                </span>
                                                            </div>
                                                        ))}
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </React.Fragment>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
                {(activity.comments.length > 0 || activity.waUpdates.length > 0) && (
                    <div className={styles.twoCol} style={{ marginTop: 16 }}>
                        <div>
                            <div className={styles.toolSection}>Comment replies</div>
                            {activity.comments.slice(0, 10).map((c) => (
                                <div key={c._id} className={styles.hint}>
                                    {fmt(c.at)} · {c.fromName}: “{(c.text || '').slice(0, 60)}” → {c.error ? `⚠ ${c.error}` : `replied${c.inboxSent ? ' + inbox' : ''}`}
                                </div>
                            ))}
                        </div>
                        <div>
                            <div className={styles.toolSection}>WhatsApp updates</div>
                            {activity.waUpdates.slice(0, 10).map((n) => (
                                <div key={n._id} className={styles.hint}>
                                    {fmt(n.at)} · {n.event} · {n.to} → {n.status}
                                    {n.error ? ` (${n.error})` : ''}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            <div className={styles.saveBar}>
                <span className={styles.muted}>{dirty ? 'You have unsaved changes' : 'All changes saved'}</span>
                <button type="button" className={styles.btnPrimary} onClick={save} disabled={saving || !dirty}>
                    {saving ? 'Saving…' : 'Save changes'}
                </button>
            </div>
        </div>
    );
}

export default ChatAutomation;
